// Tabla de precios que Diego edita. Precio APRENDIDO de cotizaciones pasadas: precio de lista por watt según el tamaño
// (rangos de kWp) + costos fijos (trámites; UVIE + UI ~$30,000 según capacidad en GDMTO/GDMTH; viáticos) + costos
// internos para la rentabilidad. Cada valor lleva su fuente y si Diego ya lo confirmó: lo que no está confirmado se
// usa, pero la rentabilidad sale marcada "con costos sin confirmar" (aviso COSTO_SIN_CONFIRMAR).
// ADENDA A0b (10-oct-2026): la tabla ADOPTADA manda en USD/W de CONTADO por tamaño (tipo_cambio = FIX + 3 %, congelado
// por folio en ParametrosCalculo.tipo_cambio), con un PISO por rango que solo abre Diego, y los costos POR TAMAÑO
// (costos_por_tamano) de COSTOS-DIEGO-2026-10-10.md para que el semáforo de margen sirva también en proyectos grandes.
// Todo lo nuevo es opcional: una tabla de antes (en pesos, un solo costo) sigue validando y el motor la usa como antes.
import { z } from 'zod';
import { Fecha, FechaHora, Id, Pesos, Tarifa, Texto, TipoProyecto } from './basicos.ts';
import { DESCUENTO_MAX_SIN_APROBACION_INTERNOS_POR_OMISION, DESCUENTO_MAX_SIN_APROBACION_POR_OMISION, MSI_TOPE_LISTA_CON_IVA, PRECIO_UNICO_ARRIBA_DE_POR_OMISION, UMBRALES_COSTOS_FIJOS_POR_OMISION } from '../constantes.ts';
import { puedeAutorizarDescuento } from './usuario.ts';
import type { Usuario } from './usuario.ts';

export const RangoPrecio = z.strictObject({
  kwp_desde: z.number().nonnegative(),
  /** null = sin tope. */
  kwp_hasta: z.number().positive().nullable(),
  /** Precio de LISTA por watt pico, sin IVA, en pesos (incluye equipo, estructura, instalación, trámite CFE de casa,
   *  monitoreo: "llave en mano"). El contado es lista × (1 − descuento_contado_pct). Con `contado_usd_por_w` (tabla en
   *  dólares) este número es solo la referencia al tipo de cambio de la tabla: el motor calcula con el del folio. */
  precio_lista_sin_iva_por_w: z.number().positive().max(100),
  /** ADENDA A0b: precio de CONTADO por watt, sin IVA, en DÓLARES (la tabla adoptada manda en USD/W). Pesos = USD/W ×
   *  tipo de cambio del folio; lista = contado ÷ (1 − descuento_contado_pct). Obligatorio si la tabla trae tipo_cambio. */
  contado_usd_por_w: z.number().positive().max(5).optional(),
  /** ADENDA A0b: el PISO del contado por watt (USD/W sin IVA) — "candado que solo abre Diego". Debajo, aviso ROJO
   *  DEBAJO_DEL_PISO. Ausente = sin piso en este rango. */
  piso_contado_usd_por_w: z.number().positive().max(5).optional(),
  fuente: Texto(2, 300),
});
export type RangoPrecio = z.infer<typeof RangoPrecio>;

/** ADENDA A0b: tipo de cambio = FIX de Banxico + colchón (3 %). Diego lo actualiza en Ajustes; cada folio congela el
 *  suyo al crearse (ParametrosCalculo.tipo_cambio). */
export const TipoCambioTabla = z.strictObject({
  /** FIX de Banxico (pesos por dólar). */
  fix: z.number().min(5).max(60),
  fecha_fix: Fecha,
  colchon_pct: z.number().min(0).max(20),
  fuente: Texto(2, 300),
});
export type TipoCambioTabla = z.infer<typeof TipoCambioTabla>;

/** Pesos por dólar con el colchón (4 decimales). */
export function tipoCambioConColchon(tc: Pick<TipoCambioTabla, 'fix' | 'colchon_pct'>): number {
  return Math.round(tc.fix * (1 + tc.colchon_pct / 100) * 1e4) / 1e4;
}

/** Diego, 12-oct (punto 4 + respuestas del mediodía, MANDAN): costo POR PANEL de una estructura que no es la de losa/lámina
 *  (esa es `estructura_usd_por_panel` + `mano_obra_por_panel_sin_iva` de CostosPorTamano). Reemplaza al de la losa en esa
 *  obra; lo que cuesta DE MÁS sube el precio del sistema para cuidar el margen. Todo sin IVA. */
export const CostoEstructura = z.strictObject({
  /** Estructura por panel, en dólares (carport 230, suelo 100). null = la MISMA de la losa/lámina
   *  (`CostosPorTamano.estructura_usd_por_panel`, la sigue si Diego la cambia): el PTR lleva el aluminio de siempre sobre su
   *  base de acero (hallazgo del 12-oct: con 60 fijos, subir la losa a 73 BAJABA el precio del PTR). */
  estructura_usd_por_panel: z.number().nonnegative().max(1_000).nullable(),
  /** Herrería por panel, en dólares (PTR: 36, la "estructura elevada de acero" del catálogo de COSTOS-DIEGO; las demás 0). */
  herreria_usd_por_panel: z.number().nonnegative().max(1_000),
  /** Mano de obra por panel, en pesos, por destajo (PTR, suelo y carport 800; Diego confirmó suelo y carport el 13-oct). */
  mano_obra_por_panel_sin_iva: Pesos,
  /** true = Diego todavía no da el dato: la cotización lleva un aviso amarillo "por confirmar" (sin cifras de costo). */
  por_confirmar: z.boolean(),
  fuente: Texto(2, 300),
});
export type CostoEstructura = z.infer<typeof CostoEstructura>;
export const EstructurasEspeciales = z.strictObject({ ptr: CostoEstructura, suelo: CostoEstructura, carport: CostoEstructura });
export type EstructurasEspeciales = z.infer<typeof EstructurasEspeciales>;

/** Lo que dijo Diego el 12-oct, para una tabla que todavía no trae `costos_por_tamano.estructuras` (la de la base viva):
 *  Diego lo edita en Configuración → Tabla de precios y desde ahí manda lo suyo. */
export const ESTRUCTURAS_POR_OMISION: Readonly<EstructurasEspeciales> = Object.freeze({
  ptr: {
    estructura_usd_por_panel: null,
    herreria_usd_por_panel: 36,
    mano_obra_por_panel_sin_iva: 800,
    por_confirmar: false,
    fuente: 'Diego, 12-oct: PTR mano de obra 800 MXN/panel en total (tarde: "de 600 a 800"; no se suman los 400 del catálogo); el aluminio de la losa (sigue al de la losa) + herrería 36 USD/panel (COSTOS-DIEGO, estructura elevada de acero).',
  },
  suelo: {
    estructura_usd_por_panel: 100,
    herreria_usd_por_panel: 0,
    mano_obra_por_panel_sin_iva: 800,
    por_confirmar: false,
    fuente: 'Diego, 12-oct: sobre suelo 100 USD/panel sin IVA (reemplaza los 60 de la losa); 13-oct: mano de obra 800 MXN/panel CONFIRMADA (igual que PTR).',
  },
  carport: {
    estructura_usd_por_panel: 230,
    herreria_usd_por_panel: 0,
    mano_obra_por_panel_sin_iva: 800,
    por_confirmar: false,
    fuente: 'Diego, 12-oct: carport 230 USD/panel sin IVA (reemplaza los 60 de la losa); 13-oct: mano de obra 800 MXN/panel CONFIRMADA (igual que PTR).',
  },
});

/** COSTOS-DIEGO-2026-10-10.md ("Decisiones finales", MANDAN): el costo de Solarix por tamaño, para la rentabilidad. El
 *  inversor sale del precio de lista del proveedor (Solar Center, sep-2026) del modelo que elige el motor
 *  (paquetes/motor/datos/equipos.json). Todo sin IVA. */
export const CostosPorTamano = z.strictObject({
  /** Paneles TCL 620 W: 0.19 USD/W con flete (Diego). */
  panel_usd_por_w: z.number().positive().max(2),
  /** Envío de los inversores Growatt por pedido (Diego: 50–100 USD; la orden sale ~75 USD arriba de la lista). */
  envio_inversores_usd: z.number().nonnegative().max(5_000),
  /** Monitoreo (Shine WiFi-X) por inversor: la lista lo cobra aparte. */
  monitoreo_usd_por_inversor: z.number().nonnegative().max(1_000),
  /** Estructura por panel (Diego: 60 USD por omisión; el vendedor ajusta con un costo adicional). */
  estructura_usd_por_panel: z.number().nonnegative().max(500),
  /** Mano de obra por panel, en pesos, por destajo (Diego: 600). */
  mano_obra_por_panel_sin_iva: Pesos,
  /** Material eléctrico (Diego: 0.08 USD/W ≈ 1,500 MXN por kWp). */
  material_electrico_usd_por_w: z.number().nonnegative().max(1),
  /** Trámite ante CFE por proyecto, en pesos (Diego: 1,000, lo pagado). */
  tramite_cfe_sin_iva: Pesos,
  /** Reserva para garantías/imprevistos como % del contado sin IVA (0 = Diego no la pidió; el margen es después de
   *  comisión, como lo define él). */
  otros_pct: z.number().min(0).max(50),
  fuente: Texto(2, 500),
  confirmado: z.boolean(),
  /** Diego, 12-oct: estructuras que no son losa/lámina (PTR, suelo, carport). Ausente = ESTRUCTURAS_POR_OMISION (lo que
   *  dijo Diego el 12-oct); léelo SIEMPRE con costoDeEstructura(). */
  estructuras: EstructurasEspeciales.optional(),
});
export type CostosPorTamano = z.infer<typeof CostosPorTamano>;

/** El costo por panel que vale para una estructura con esta tabla: losa/lámina = la estructura y la mano de obra de
 *  siempre (herrería 0); PTR, suelo y carport = los de la tabla o, si no los trae, los de Diego del 12-oct (`de_tabla`
 *  false). Una estructura sin dólares propios (null, el PTR por omisión) lleva los de la losa (`de_la_losa` true): si
 *  Diego sube la losa, el PTR sube igual. Motor, servidor e interfaz leen SOLO esto. */
export function costoDeEstructura(
  cp: Pick<CostosPorTamano, 'estructura_usd_por_panel' | 'mano_obra_por_panel_sin_iva'> & { estructuras?: EstructurasEspeciales | undefined },
  tipo: 'losa_lamina' | 'ptr' | 'suelo' | 'carport',
): { estructura_usd_por_panel: number; herreria_usd_por_panel: number; mano_obra_por_panel_sin_iva: number; por_confirmar: boolean; de_tabla: boolean; de_la_losa: boolean } {
  if (tipo === 'losa_lamina')
    return { estructura_usd_por_panel: cp.estructura_usd_por_panel, herreria_usd_por_panel: 0, mano_obra_por_panel_sin_iva: cp.mano_obra_por_panel_sin_iva, por_confirmar: false, de_tabla: true, de_la_losa: true };
  const e = cp.estructuras?.[tipo] ?? ESTRUCTURAS_POR_OMISION[tipo];
  return {
    estructura_usd_por_panel: e.estructura_usd_por_panel ?? cp.estructura_usd_por_panel,
    herreria_usd_por_panel: e.herreria_usd_por_panel,
    mano_obra_por_panel_sin_iva: e.mano_obra_por_panel_sin_iva,
    por_confirmar: e.por_confirmar,
    de_tabla: !!cp.estructuras,
    de_la_losa: e.estructura_usd_por_panel === null,
  };
}

/** Catálogo de adicionales que ya cobran una estructura especial: con PTR, suelo o carport en el cuestionario NO se pueden
 *  sumar (doble cobro; el motor lo marca en rojo y el servidor no deja agregarlos). */
export const ADICIONALES_DE_ESTRUCTURA: readonly string[] = Object.freeze(['estructura-elevada', 'estructura-suelo', 'estructura-especial']);

export const CostoFijo = z.strictObject({
  id: z.string().regex(/^[a-z0-9_-]{2,40}$/),
  concepto: Texto(2, 120),
  /** Se suma al precio de lista (sin IVA). 0 si va incluido en el precio por watt y solo cuenta como costo. */
  precio_cliente_sin_iva: Pesos,
  /** Lo que le cuesta a Solarix (sin IVA), para la rentabilidad. */
  costo_solarix_sin_iva: Pesos,
  /** Cuándo aplica. null en un campo = aplica a todos. */
  aplica: z.strictObject({
    tipos: z.array(TipoProyecto).nullable(),
    tarifas: z.array(Tarifa).nullable(),
    kwp_desde: z.number().nonnegative().nullable(),
    kwp_hasta: z.number().positive().nullable(),
  }),
  /** Renglones que se compran en DÓLARES (medidor de cero exportación: ~200 USD, COSTOS-DIEGO): precio de LISTA y costo
   *  en USD sin IVA. Con ellos el motor ignora los montos en pesos y convierte con el tipo de cambio del folio. */
  precio_cliente_usd_sin_iva: z.number().nonnegative().max(1_000_000).optional(),
  costo_solarix_usd_sin_iva: z.number().nonnegative().max(1_000_000).optional(),
  fuente: Texto(2, 300),
  confirmado: z.boolean(),
});
export type CostoFijo = z.infer<typeof CostoFijo>;

export const CostosInternos = z.strictObject({
  /** Paneles + inversores + estructura, por watt pico, sin IVA. */
  equipo_por_w_sin_iva: z.number().nonnegative(),
  mano_obra_por_panel_sin_iva: Pesos,
  material_electrico_por_kwp_sin_iva: Pesos,
  /** Comisión de venta sobre el precio de contado sin IVA (vendedores externos). */
  comision_venta_pct: z.number().min(0).max(50),
  /** Otros gastos como % del contado sin IVA (administración, garantías, imprevistos). */
  otros_pct: z.number().min(0).max(50),
  fuente: Texto(2, 300),
  confirmado: z.boolean(),
});
export type CostosInternos = z.infer<typeof CostosInternos>;

/** COSTOS-DIEGO (10-oct, "agregar al catálogo"): los costos adicionales frecuentes con su COSTO para Solarix, para que el
 *  vendedor los agregue con un toque (grúa, cable de acometida, estructura elevada, mufa, viáticos…). El precio de contado
 *  sugerido sale del costo: ÷ 0.80 si el trabajo lo hace Solarix ("solarix") o ÷ 0.94 si solo pasa por Solarix ("pasa":
 *  cubre la comisión); se captura SIN descuento de contado y el motor sube su lista ÷ 0.85 (ADENDA A0b). */
export const AdicionalCatalogo = z.strictObject({
  id: z.string().regex(/^[a-z0-9_-]{2,40}$/),
  concepto: Texto(2, 120),
  unidad: z.enum(['proyecto', 'pieza', 'panel', 'kwp', 'metro', 'maniobra']),
  /** Costo por unidad en pesos sin IVA; null = lo captura el vendedor (lo que le pasa el técnico). */
  costo_mxn: Pesos.nullable(),
  /** Parte del costo por unidad que se paga en dólares (se suma convertida con el TC del folio). */
  costo_usd: z.number().nonnegative().max(100_000).optional(),
  rango_costo_mxn: z.tuple([Pesos, Pesos]).nullable(),
  margen: z.enum(['solarix', 'pasa']),
  nota: z.string().trim().max(300).nullable(),
  fuente: Texto(2, 300),
  confirmado: z.boolean(),
});
export type AdicionalCatalogo = z.infer<typeof AdicionalCatalogo>;

/** Costo y precio de CONTADO sugeridos por unidad de un adicional del catálogo (sin IVA). null si el costo lo captura el
 *  vendedor. Se agrega como costo adicional SIN descuento de contado (el motor sube su lista ÷ 0.85). */
export function precioSugeridoAdicional(a: Pick<AdicionalCatalogo, 'costo_mxn' | 'costo_usd' | 'margen'>, tc: number | null): { costo: number; contado: number } | null {
  if (a.costo_mxn === null && !a.costo_usd) return null;
  if (a.costo_usd && tc === null) return null;
  const costo = Math.round(((a.costo_mxn ?? 0) + (a.costo_usd ?? 0) * (tc ?? 0)) * 100) / 100;
  const contado = Math.round((costo / (a.margen === 'solarix' ? 0.8 : 0.94)) * 100) / 100;
  return { costo, contado };
}

/** Contrato v2 · BATERÍAS (ESPEC 3.4; marcas de Diego: Fortress, Growatt, Huawei). Precio de un MÓDULO de batería.
 *  Sin renglones aquí el motor NO inventa precio: el nivel sale sin precio y con aviso RESPALDO_SIN_PRECIO. La
 *  instalación y el tablero de respaldo van como costos_fijos con aplica.tipos ["baterias", "aislado"]. */
export const PrecioBateria = z.strictObject({
  id: z.string().regex(/^[a-z0-9_-]{2,40}$/),
  marca: Texto(2, 40),
  modelo: Texto(2, 60),
  /** "LFP", etc. */
  quimica: Texto(2, 20),
  kwh_nominales_por_modulo: z.number().positive().max(100),
  kwh_utiles_por_modulo: z.number().positive().max(100),
  modulos_max: z.int().min(1).max(64),
  precio_lista_sin_iva_por_modulo: Pesos,
  /** null = no se sabe: la rentabilidad lo toma igual al precio y avisa COSTO_SIN_CONFIRMAR. */
  costo_solarix_sin_iva_por_modulo: Pesos.nullable(),
  /** Años de garantía del fabricante: Growatt AXE 5, ampliables a 10 con su garantía extendida (ADENDA z2, 14-oct-2026; z22,
   *  16-oct: SOLO la AXE); las demás Growatt (APX, ARK), Fortress, Huawei, Victron y Pylontech 10 (adenda A2 y ADENDA z3). Lo que imprime el PDF sale de la plantilla
   *  (lib/nucleo/garantias.json), no de este campo. */
  garantia_anios: z.int().min(1).max(30),
  fuente: Texto(2, 300),
  confirmado: z.boolean(),
});
export type PrecioBateria = z.infer<typeof PrecioBateria>;

/** Contrato v2 · inversor híbrido (o de respaldo) para el banco de baterías. */
export const PrecioInversorHibrido = z.strictObject({
  id: z.string().regex(/^[a-z0-9_-]{2,40}$/),
  marca: Texto(2, 40),
  modelo: Texto(2, 60),
  kw_continuos: z.number().positive().max(1_000),
  /** Potencia PICO (de arranque, unos segundos) de la ficha. Diego (11-oct noche): el "3 × el consumo por hora del mes de
   *  más uso" es para el PICO del inversor, no para la continua. Ausente/null = la ficha no la dice: se toma la continua. */
  kw_pico: z.number().positive().max(2_000).nullable().optional(),
  /** Tiempo de cambio a respaldo en milisegundos (ESPEC 3.4 "el cambio es automático, en {ms} ms"). null = no se sabe. */
  ms_cambio: z.number().nonnegative().max(1_000).nullable(),
  precio_lista_sin_iva: Pesos,
  costo_solarix_sin_iva: Pesos.nullable(),
  garantia_anios: z.int().min(1).max(30),
  fuente: Texto(2, 300),
  confirmado: z.boolean(),
});
export type PrecioInversorHibrido = z.infer<typeof PrecioInversorHibrido>;

/** Base sin reglas cruzadas (zod no deja hacer .omit() sobre un esquema con refinamientos). */
export const TablaPreciosBase = z.strictObject({
    version: z.int().min(1),
    vigente_desde: FechaHora,
    /** id del usuario que la guardó, o "sistema" para la semilla aprendida. */
    editada_por: z.union([Id, z.literal('sistema')]),
    nota: z.string().trim().max(500).nullable(),
    descuento_contado_pct: z.number().min(0).max(30),
    /** ADENDA A0b: generación conservadora en toda Sonora para todo lo que se promete (kWh por kWp al año), "editable en
     *  Ajustes". Ausente = 1,750. La plantilla del PDF no acepta más de 1,750 (truena arriba) y avisa debajo de 1,741. */
    generacion_kwh_kwp: z.number().min(1_200).max(1_750).optional(),
    rangos: z.array(RangoPrecio).min(1).max(30),
    costos_fijos: z.array(CostoFijo).max(60),
    costos_internos: CostosInternos,
    umbrales: z.strictObject({
      /** Debajo de este margen (sobre contado sin IVA): ROJO "se pierde dinero" si < 0, ROJO "margen bajo" si < mínimo. */
      margen_minimo_pct: z.number().min(-100).max(100),
      /** Debajo de este margen: AMARILLO. */
      margen_alerta_pct: z.number().min(-100).max(100),
      /** Precio por watt fuera de ± este % del rango aprendido: AMARILLO. */
      tolerancia_precio_por_w_pct: z.number().min(0).max(100),
      /** Retorno (contado ÷ ahorro anual) arriba de esto: AMARILLO; arriba de 25 años el PDF truena. */
      retorno_alerta_anios: z.number().positive().max(25),
      /** Contrato v2 · COSTOS FIJOS: Σ precio al cliente sin IVA de los costos fijos que aplican ÷ precio de lista sin
       *  IVA de la opción × 100. Desde este %: AMARILLO (FIJOS_ENCARECEN). Van los dos o ninguno; sin ellos valen
       *  UMBRALES_COSTOS_FIJOS_POR_OMISION (20 / 35, sin confirmar). Léelos SIEMPRE con umbralesCostosFijos(). */
      costos_fijos_alerta_pct: z.number().positive().max(100).optional(),
      /** Desde este %: ROJO. Debe ser mayor que costos_fijos_alerta_pct. */
      costos_fijos_rojo_pct: z.number().positive().max(100).optional(),
      /** ADENDA A0b: hasta cuánto % puede BAJAR un vendedor el precio de lista de una opción contra la tabla sin el OK de
       *  Diego (subir es libre). Ausente = DESCUENTO_MAX_SIN_APROBACION_POR_OMISION (5). Léelo con descuentoMaxSinAprobacion(). */
      descuento_max_sin_aprobacion_pct: z.number().min(0).max(50).optional(),
      /** Diego, 13-oct: hasta cuánto % bajan SIN OK Diego, Miguel y Juan Carlos (vendedorBajaComoInterno), aunque el
       *  semáforo deje de estar verde. Ausente = DESCUENTO_MAX_SIN_APROBACION_INTERNOS_POR_OMISION (10). Léelo con
       *  descuentoMaxSinAprobacionPara(). */
      descuento_max_sin_aprobacion_internos_pct: z.number().min(0).max(50).optional(),
      /** PRECIO ÚNICO (Diego, 13-oct; ADENDA z5, 14-oct): en comercio e industria, si el contado CON IVA de la tabla de UNA
       *  opción pasa de este monto, TODAS las opciones de la propuesta llevan un solo precio (el de contado, sin lista ni
       *  descuento). Ausente = PRECIO_UNICO_ARRIBA_DE_POR_OMISION
       *  ($1,000,000). Nunca debajo del tope de MSI ($500,000): un dedazo de 1,000 dejaría a todo negocio sin MSI. Léelo con
       *  precioUnicoArribaDe(). */
      precio_unico_arriba_de_con_iva: z.number().min(MSI_TOPE_LISTA_CON_IVA).max(1_000_000_000).optional(),
    }),
    /** ADENDA A0b: la tabla manda en USD/W (rangos[].contado_usd_por_w) con este tipo de cambio. Ausente = tabla en pesos. */
    tipo_cambio: TipoCambioTabla.optional(),
    /** COSTOS-DIEGO (10-oct): costos por tamaño para la rentabilidad. Ausente = el costo único de costos_internos. */
    costos_por_tamano: CostosPorTamano.optional(),
    /** COSTOS-DIEGO (10-oct): catálogo de costos adicionales frecuentes (botón rápido del vendedor). */
    catalogo_adicionales: z.array(AdicionalCatalogo).max(40).optional(),
    panel_por_omision: z.strictObject({ marca: Texto(2, 40), modelo: Texto(2, 60), w: z.number().positive().max(1_000) }),
    /** Contrato v2 · baterías (opcional: sin esto, el respaldo sale sin precio y con aviso). */
    baterias: z.array(PrecioBateria).max(60).optional(),
    inversores_hibridos: z.array(PrecioInversorHibrido).max(60).optional(),
    /** De dónde salió lo aprendido (sin nombres de clientes). null si la tabla es manual. */
    aprendido: z
      .strictObject({
        n_cotizaciones: z.int().min(1),
        periodo: Texto(2, 60),
        metodo: Texto(2, 300),
        medido_el: Fecha,
      })
      .nullable(),
  });

/** Reglas cruzadas de la tabla (rangos continuos, umbrales coherentes, ids únicos). */
export function revisarTablaPrecios(
  t: Pick<z.infer<typeof TablaPreciosBase>, 'rangos' | 'umbrales' | 'costos_fijos'> & Partial<Pick<z.infer<typeof TablaPreciosBase>, 'baterias' | 'inversores_hibridos' | 'tipo_cambio'>>,
  ctx: z.RefinementCtx,
): void {
    // ADENDA A0b: una tabla en dólares trae el contado en USD/W en TODOS los rangos (si no, un rango saldría en pesos viejos
    // en silencio) y el piso nunca arriba del contado.
    if (t.tipo_cambio)
      t.rangos.forEach((x, i) => {
        if (x.contado_usd_por_w === undefined)
          ctx.addIssue({ code: 'custom', path: ['rangos', i, 'contado_usd_por_w'], message: `La tabla va en dólares (trae tipo de cambio) y el rango ${x.kwp_desde}–${x.kwp_hasta ?? '∞'} kWp no trae su contado en USD/W.` });
      });
    t.rangos.forEach((x, i) => {
      if (x.piso_contado_usd_por_w !== undefined && x.contado_usd_por_w !== undefined && x.piso_contado_usd_por_w > x.contado_usd_por_w)
        ctx.addIssue({ code: 'custom', path: ['rangos', i, 'piso_contado_usd_por_w'], message: `Rango ${x.kwp_desde}–${x.kwp_hasta ?? '∞'} kWp: el piso (${x.piso_contado_usd_por_w} USD/W) no puede ser mayor que el contado de la tabla (${x.contado_usd_por_w} USD/W).` });
      if (x.piso_contado_usd_por_w !== undefined && x.contado_usd_por_w === undefined)
        ctx.addIssue({ code: 'custom', path: ['rangos', i, 'piso_contado_usd_por_w'], message: `Rango ${x.kwp_desde}–${x.kwp_hasta ?? '∞'} kWp: el piso va en USD/W y el rango no trae su contado en USD/W.` });
    });
    if (!t.tipo_cambio && t.costos_fijos.some((f) => f.precio_cliente_usd_sin_iva !== undefined || f.costo_solarix_usd_sin_iva !== undefined))
      ctx.addIssue({ code: 'custom', path: ['costos_fijos'], message: 'Hay costos fijos en dólares y la tabla no trae tipo de cambio.' });
    const r = [...t.rangos].sort((a, b) => a.kwp_desde - b.kwp_desde);
    if (r[0] && r[0].kwp_desde !== 0) ctx.addIssue({ code: 'custom', path: ['rangos'], message: 'El primer rango debe empezar en 0 kWp.' });
    for (let i = 0; i < r.length; i++) {
      const a = r[i]!;
      const b = r[i + 1];
      if (a.kwp_hasta !== null && a.kwp_hasta <= a.kwp_desde)
        ctx.addIssue({ code: 'custom', path: ['rangos', i], message: `Rango ${a.kwp_desde}–${a.kwp_hasta} kWp al revés.` });
      if (b && a.kwp_hasta !== b.kwp_desde)
        ctx.addIssue({ code: 'custom', path: ['rangos', i], message: `Los rangos deben ser continuos: termina en ${a.kwp_hasta} y el siguiente empieza en ${b.kwp_desde}.` });
      if (!b && a.kwp_hasta !== null) ctx.addIssue({ code: 'custom', path: ['rangos', i], message: 'El último rango debe ir sin tope (kwp_hasta: null).' });
    }
    if (t.umbrales.margen_alerta_pct < t.umbrales.margen_minimo_pct)
      ctx.addIssue({ code: 'custom', path: ['umbrales'], message: 'El margen de alerta no puede ser menor que el mínimo.' });
    const ids = new Set<string>();
    for (const f of t.costos_fijos) {
      if (ids.has(f.id)) ctx.addIssue({ code: 'custom', path: ['costos_fijos'], message: `Costo fijo repetido: ${f.id}` });
      ids.add(f.id);
    }
    const { costos_fijos_alerta_pct: fa, costos_fijos_rojo_pct: fr } = t.umbrales;
    if ((fa === undefined) !== (fr === undefined))
      ctx.addIssue({ code: 'custom', path: ['umbrales'], message: 'Los umbrales de costos fijos van los dos (alerta y rojo) o ninguno.' });
    if (fa !== undefined && fr !== undefined && fr <= fa)
      ctx.addIssue({ code: 'custom', path: ['umbrales', 'costos_fijos_rojo_pct'], message: `El umbral rojo de costos fijos (${fr} %) debe ser mayor que el de alerta (${fa} %).` });
    const idsBat = new Set<string>();
    for (const [i, b] of (t.baterias ?? []).entries()) {
      if (idsBat.has(b.id)) ctx.addIssue({ code: 'custom', path: ['baterias', i, 'id'], message: `Batería repetida: ${b.id}` });
      idsBat.add(b.id);
      if (b.kwh_utiles_por_modulo > b.kwh_nominales_por_modulo)
        ctx.addIssue({ code: 'custom', path: ['baterias', i, 'kwh_utiles_por_modulo'], message: `${b.marca} ${b.modelo}: los kWh útiles no pueden pasar de los nominales.` });
    }
    const idsInv = new Set<string>();
    for (const [i, v] of (t.inversores_hibridos ?? []).entries()) {
      if (idsInv.has(v.id)) ctx.addIssue({ code: 'custom', path: ['inversores_hibridos', i, 'id'], message: `Inversor híbrido repetido: ${v.id}` });
      idsInv.add(v.id);
      if (v.kw_pico !== undefined && v.kw_pico !== null && v.kw_pico < v.kw_continuos)
        ctx.addIssue({ code: 'custom', path: ['inversores_hibridos', i, 'kw_pico'], message: `${v.marca} ${v.modelo}: el pico (${v.kw_pico} kW) no puede ser menor que la potencia continua (${v.kw_continuos} kW).` });
    }
}

/** Umbrales de costos fijos que VALEN para esta tabla: los suyos o, si no los trae, los de omisión (sin confirmar).
 *  `de_tabla` dice cuál (la pantalla de precios lo enseña: "por omisión, sin confirmar"). Motor e IA leen SOLO esto. */
export function umbralesCostosFijos(tabla: { umbrales: Pick<z.infer<typeof TablaPreciosBase>['umbrales'], 'costos_fijos_alerta_pct' | 'costos_fijos_rojo_pct'> }): {
  alerta_pct: number;
  rojo_pct: number;
  de_tabla: boolean;
} {
  const { costos_fijos_alerta_pct: a, costos_fijos_rojo_pct: r } = tabla.umbrales;
  if (a !== undefined && r !== undefined) return { alerta_pct: a, rojo_pct: r, de_tabla: true };
  if (a !== undefined || r !== undefined) throw new Error('umbralesCostosFijos: la tabla trae solo uno de los dos umbrales de costos fijos (no debió validar).');
  return { alerta_pct: UMBRALES_COSTOS_FIJOS_POR_OMISION.alerta_pct, rojo_pct: UMBRALES_COSTOS_FIJOS_POR_OMISION.rojo_pct, de_tabla: false };
}

/** Tope de descuento sin aprobación de un vendedor EXTERNO con esta tabla (ADENDA A0b: 5 % si la tabla no dice otro). */
export function descuentoMaxSinAprobacion(tabla: { umbrales: { descuento_max_sin_aprobacion_pct?: number | undefined } }): number {
  return tabla.umbrales.descuento_max_sin_aprobacion_pct ?? DESCUENTO_MAX_SIN_APROBACION_POR_OMISION;
}

/** Tope de Diego, Miguel y Juan Carlos con esta tabla (Diego, 13-oct: 10 % si la tabla no dice otro). */
export function descuentoMaxSinAprobacionInternos(tabla: { umbrales: { descuento_max_sin_aprobacion_internos_pct?: number | undefined } }): number {
  return tabla.umbrales.descuento_max_sin_aprobacion_internos_pct ?? DESCUENTO_MAX_SIN_APROBACION_INTERNOS_POR_OMISION;
}

/** Diego, 13-oct: ¿este vendedor baja con el tope de internos (hasta 10 % sin OK, sin "verde manda")? Los mismos que
 *  pueden autorizar un descuento: Diego (aprobar_precios) y los asesores internos Miguel Cota y Juan Carlos Cortés
 *  (autorizar_descuentos). Un vendedor de Optimus o un externo, no. Sin valor por omisión a propósito: quien llama dice
 *  de quién es la cotización. */
export function vendedorBajaComoInterno(u: Pick<Usuario, 'rol' | 'activo'> & { optimus?: boolean | undefined }): boolean {
  return puedeAutorizarDescuento(u);
}

/** El tope que vale para ESTE vendedor con esta tabla (el de internos o el de externos). */
export function descuentoMaxSinAprobacionPara(
  tabla: { umbrales: { descuento_max_sin_aprobacion_pct?: number | undefined; descuento_max_sin_aprobacion_internos_pct?: number | undefined } },
  interno: boolean,
): number {
  return interno ? descuentoMaxSinAprobacionInternos(tabla) : descuentoMaxSinAprobacion(tabla);
}

/** Umbral del precio único que vale para esta tabla (Diego, 13-oct: $1,000,000 con IVA si la tabla no dice otro). */
export function precioUnicoArribaDe(tabla: { umbrales: { precio_unico_arriba_de_con_iva?: number | undefined } }): number {
  return tabla.umbrales.precio_unico_arriba_de_con_iva ?? PRECIO_UNICO_ARRIBA_DE_POR_OMISION;
}

export const TablaPrecios = TablaPreciosBase.superRefine(revisarTablaPrecios);
export type TablaPrecios = z.infer<typeof TablaPrecios>;

/** Lo que manda la pantalla de precios al guardar (el servidor pone versión, fecha y autor). */
export const CuerpoTablaPrecios = TablaPreciosBase.omit({ version: true, vigente_desde: true, editada_por: true }).superRefine(revisarTablaPrecios);
export type CuerpoTablaPrecios = z.infer<typeof CuerpoTablaPrecios>;

// ───────────── Vista de la tabla para quien NO ve costos (ADENDA A0b, 11-oct) ─────────────

/** Un adicional del catálogo como lo ve el vendedor: concepto, unidad y PRECIO de contado sugerido por unidad (sin IVA,
 *  con el tipo de cambio de la tabla). Nunca el costo: el servidor lo pone al agregar el costo (catalogo_id). */
export const ItemCatalogoVista = z.strictObject({
  id: z.string().regex(/^[a-z0-9_-]{2,40}$/),
  concepto: Texto(2, 120),
  unidad: AdicionalCatalogo.shape.unidad,
  /** null = el monto lo captura el vendedor (lo que le pasa el técnico). */
  precio_contado_sugerido: Pesos.nullable(),
  rango_precio_contado: z.tuple([Pesos, Pesos]).nullable(),
  nota: z.string().trim().max(300).nullable(),
});
export type ItemCatalogoVista = z.infer<typeof ItemCatalogoVista>;

export const VistaPrecios = z.strictObject({
  version: z.int().min(1),
  vigente_desde: FechaHora,
  descuento_contado_pct: z.number().min(0).max(30),
  /** Hasta cuánto baja SIN OK la persona que entra (subir es libre). Diego, 13-oct: Diego, Miguel y Juan Carlos, el tope de
   *  internos (10 %); los demás, el de siempre (5 %). */
  descuento_max_sin_aprobacion_pct: z.number().min(0).max(50),
  /** Verificación adversarial (13-oct): los DOS topes, para que la pantalla mida con el VENDEDOR de la cotización (Miguel
   *  abriendo la de un externo: 5; un externo no abre las de los internos). El de arriba es el de quien entra. */
  descuento_max_sin_aprobacion_internos_pct: z.number().min(0).max(50),
  descuento_max_sin_aprobacion_externos_pct: z.number().min(0).max(50),
  /** Pesos por dólar con el colchón (null = tabla en pesos) y el FIX del que sale. */
  tipo_cambio: z.number().positive().nullable(),
  fecha_fix: Fecha.nullable(),
  rangos: z.array(
    z.strictObject({
      kwp_desde: z.number().nonnegative(),
      kwp_hasta: z.number().positive().nullable(),
      contado_usd_por_w: z.number().positive().nullable(),
      contado_mxn_por_w: z.number().positive(),
      lista_mxn_por_w: z.number().positive(),
    }),
  ),
  panel: z.strictObject({ marca: Texto(2, 40), modelo: Texto(2, 60), w: z.number().positive().max(1_000) }),
  catalogo: z.array(ItemCatalogoVista).max(40),
  /** Baterías (Diego, 13-oct): las baterías y los inversores híbridos de la tabla, SIN costos, para que el vendedor elija
   *  el equipo del respaldo (parametros.equipo_respaldo). Ausente = servidor de antes. */
  equipos_respaldo: z
    .strictObject({
      baterias: z.array(z.strictObject({ id: z.string(), marca: Texto(2, 40), modelo: Texto(2, 60), kwh_utiles_por_modulo: z.number().positive() })),
      inversores: z.array(z.strictObject({ id: z.string(), marca: Texto(2, 40), modelo: Texto(2, 60), kw_continuos: z.number().positive() })),
    })
    .optional(),
  /** Ampliación de baterías (27-oct-2026): las baterías que se pueden SUMAR, con lo de su ficha que necesita la cotización y SIN
   *  costos (`costo_en_tabla` dice si la tabla trae su costo: si no, el vendedor teclea el del proveedor). Las de la tabla de
   *  precios y las del catálogo del motor que la tabla no trae (id_tabla null). Ausente = servidor de antes. */
  baterias_ampliacion: z
    .array(
      z.strictObject({
        id_tabla: z.string().nullable(),
        marca: z.string().trim().min(2).max(30),
        modelo: z.string().trim().min(1).max(40),
        kwh_nominales_por_modulo: z.number().positive().max(100),
        modulos_max: z.int().min(2).max(64),
        garantia_anios: z.int().min(1).max(30),
        costo_en_tabla: z.boolean(),
      }),
    )
    .max(80)
    .optional(),
});
export type VistaPrecios = z.infer<typeof VistaPrecios>;

const r2 = (n: number) => Math.round(n * 100) / 100;

/** La tabla sin costos ni piso: lo que cualquier vendedor puede ver (precios por tamaño, tope de descuento, catálogo con
 *  precio sugerido). La arma el servidor; el director recibe además la tabla completa. `factor` (OPTIMUS, Diego 12-oct
 *  noche): un vendedor de Optimus ve SUS precios (tabla × factor, también el catálogo): el USD/W que ve es el de Optimus. */
export function vistaPrecios(t: TablaPrecios, factor: number, interno: boolean, catalogoBaterias: readonly CatalogoBateriaAmpliacion[] = []): VistaPrecios {
  if (!(factor >= 1 && factor <= 2)) throw new Error(`vistaPrecios: factor imposible (${factor})`);
  const tc = t.tipo_cambio ? tipoCambioConColchon(t.tipo_cambio) : null;
  const f = 1 - t.descuento_contado_pct / 100;
  return VistaPrecios.parse({
    version: t.version,
    vigente_desde: t.vigente_desde,
    descuento_contado_pct: t.descuento_contado_pct,
    // Diego, 13-oct: el tope de la persona que entra (Diego, Miguel y Juan Carlos: el de internos; los demás, el de siempre)
    descuento_max_sin_aprobacion_pct: descuentoMaxSinAprobacionPara(t, interno),
    descuento_max_sin_aprobacion_internos_pct: descuentoMaxSinAprobacionInternos(t),
    descuento_max_sin_aprobacion_externos_pct: descuentoMaxSinAprobacion(t),
    tipo_cambio: tc,
    fecha_fix: t.tipo_cambio?.fecha_fix ?? null,
    rangos: t.rangos.map((r) => {
      const contadoMxn = (r.contado_usd_por_w !== undefined && tc !== null ? r.contado_usd_por_w * tc : r.precio_lista_sin_iva_por_w * f) * factor;
      return {
        kwp_desde: r.kwp_desde,
        kwp_hasta: r.kwp_hasta,
        contado_usd_por_w: r.contado_usd_por_w !== undefined ? Math.round(r.contado_usd_por_w * factor * 1e4) / 1e4 : null,
        contado_mxn_por_w: Math.round(contadoMxn * 1e4) / 1e4,
        lista_mxn_por_w: Math.round((contadoMxn / f) * 1e4) / 1e4,
      };
    }),
    panel: { ...t.panel_por_omision },
    catalogo: (t.catalogo_adicionales ?? []).map((a) => {
      const div = a.margen === 'solarix' ? 0.8 : 0.94;
      const sug = precioSugeridoAdicional(a, tc);
      return {
        id: a.id,
        concepto: a.concepto,
        unidad: a.unidad,
        precio_contado_sugerido: sug ? r2(sug.contado * factor) : null,
        rango_precio_contado: a.rango_costo_mxn ? [r2((a.rango_costo_mxn[0] / div) * factor), r2((a.rango_costo_mxn[1] / div) * factor)] : null,
        nota: a.nota,
      };
    }),
    equipos_respaldo: {
      baterias: (t.baterias ?? []).map((b) => ({ id: b.id, marca: b.marca, modelo: b.modelo, kwh_utiles_por_modulo: b.kwh_utiles_por_modulo })),
      inversores: (t.inversores_hibridos ?? []).map((v) => ({ id: v.id, marca: v.marca, modelo: v.modelo, kw_continuos: v.kw_continuos })),
    },
    baterias_ampliacion: bateriasParaAmpliacion(t, catalogoBaterias),
  });
}

/** Una batería del catálogo del motor (datos/respaldo.json → catalogo_baterias) para la ampliación, ya con el tope de módulos
 *  que usa el motor. */
export interface CatalogoBateriaAmpliacion {
  marca: string;
  modelo: string;
  kwh_nominales_por_modulo: number;
  modulos_max: number;
  garantia_anios: number;
}
const claveBateria = (b: { marca: string; modelo: string }) => `${b.marca} ${b.modelo}`.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase().replace(/[^a-z0-9]+/g, '');
/** Ampliación de baterías: las de la tabla (con si traen costo) y, después, las del catálogo que la tabla no trae. SIN costos. */
export function bateriasParaAmpliacion(t: Pick<TablaPrecios, 'baterias'>, catalogo: readonly CatalogoBateriaAmpliacion[]): NonNullable<VistaPrecios['baterias_ampliacion']> {
  const recorta = (s: string, n: number) => s.trim().slice(0, n);
  const deTabla = (t.baterias ?? []).filter((b) => b.modulos_max >= 2).map((b) => ({
    id_tabla: b.id,
    marca: recorta(b.marca, 30),
    modelo: recorta(b.modelo, 40),
    kwh_nominales_por_modulo: b.kwh_nominales_por_modulo,
    modulos_max: b.modulos_max,
    garantia_anios: b.garantia_anios,
    costo_en_tabla: (b.costo_solarix_sin_iva_por_modulo ?? 0) > 0,
  }));
  const ya = new Set(deTabla.map(claveBateria));
  const delCatalogo = catalogo.filter((b) => b.modulos_max >= 2 && !ya.has(claveBateria(b))).map((b) => ({
    id_tabla: null,
    marca: recorta(b.marca, 30),
    modelo: recorta(b.modelo, 40),
    kwh_nominales_por_modulo: b.kwh_nominales_por_modulo,
    modulos_max: b.modulos_max,
    garantia_anios: b.garantia_anios,
    costo_en_tabla: false,
  }));
  return [...deTabla, ...delCatalogo].slice(0, 80);
}

/** Rango que le toca a un tamaño de sistema. Truena si la tabla no cubre ese tamaño (no debe pasar si validó). */
export function rangoPara(tabla: Pick<TablaPrecios, 'rangos'>, kwp: number): RangoPrecio {
  const r = tabla.rangos.find((x) => kwp >= x.kwp_desde && (x.kwp_hasta === null || kwp < x.kwp_hasta));
  if (!r) throw new Error(`La tabla de precios no tiene rango para ${kwp} kWp`);
  return r;
}
